import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Flex, Grid, Spinner } from '@chakra-ui/react';
import { Avatar, Text } from '@kula-ai/components';
import { ArrowSquareUpRight, Mail01, Phone01 } from '@kula-ai/icons-core';
import { FaGithub, FaLinkedin, FaXTwitter } from 'react-icons/fa6';
import { ExperienceView } from '@/components/contacts/details/ExperienceView';
import { EducationView } from '@/components/contacts/details/EducationView';
import type { HostContact } from '../../../api/hostContact';
import { useCandidateProfileQuery } from '../../../api/candidates';
import { useHostContactQuery } from '../../../api/hostContact';
import { toInternalPath } from '../../../helpers';
import { useAgentForgeStore } from '../../../stores';
import { buildProfileView, type ProfileView } from './profileView';
import { ResumeSection } from './ResumeSection';
import { fromResumePayloads } from './toResumeAttachments';
import type { CandidateProfileCardProps } from './CandidateProfileCard.types';

type Experience = NonNullable<HostContact['experiences']>[number];
type Education = NonNullable<HostContact['educations']>[number];

const Dot = () => (
  <Box as="span" color="secondary-300" px="2px">
    •
  </Box>
);

const Section = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box mt="16px">
    <Text variant="h6">{label}</Text>
    <Box mt="10px">{children}</Box>
  </Box>
);

// Muted placeholder shown when a section has no data, so the header still reads.
const EmptyNote = ({ text }: { text: string }) => (
  <Text variant="p" as="span" className="text-secondary-400">
    {text}
  </Text>
);

const IconLink = ({
  href,
  label,
  color,
  hoverColor,
  children,
}: {
  href: string;
  label: string;
  color: string;
  hoverColor?: string;
  children: ReactNode;
}) => (
  <Box
    as="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    color={color}
    display="inline-flex"
    aria-label={label}
    _hover={hoverColor ? { color: hoverColor } : undefined}
  >
    {children}
  </Box>
);

// Avatar + name + social links + headline (always shown, even while loading).
const ProfileHeader = ({
  view,
  profilePath,
  onOpenProfile,
}: {
  view: ProfileView;
  profilePath: string;
  onOpenProfile: () => void;
}) => (
  <Flex gap="12px" align="flex-start">
    <Avatar src={view.avatarUrl} name={view.name} shape="square" size={48} />
    <Box flex="1" minW="0">
      <Flex align="center" gap="8px" wrap="wrap">
        <Text variant="h2" as="span">
          {view.name}
        </Text>
        {view.linkedInUrl && (
          <IconLink
            href={view.linkedInUrl}
            label="LinkedIn profile"
            color="#0A66C2"
          >
            <FaLinkedin size={15} />
          </IconLink>
        )}
        {view.twitterUrl && (
          <IconLink
            href={view.twitterUrl}
            label="X profile"
            color="secondary-900"
          >
            <FaXTwitter size={13} />
          </IconLink>
        )}
        {view.githubUrl && (
          <IconLink
            href={view.githubUrl}
            label="GitHub profile"
            color="secondary-900"
          >
            <FaGithub size={15} />
          </IconLink>
        )}
        {/* Deep-links to the candidate page and floats the chat over it (moved
            off the result-card name, which now opens this preview). RouterLink
            navigates; onClick floats the bridge — same pattern as job rows. */}
        <Box
          as={RouterLink}
          to={profilePath}
          onClick={onOpenProfile}
          color="secondary-400"
          display="inline-flex"
          aria-label="Open full profile"
          _hover={{ color: 'primary-500' }}
        >
          <ArrowSquareUpRight size={15} />
        </Box>
      </Flex>
      {view.headline && (
        <Box mt="2px">
          <Text variant="p" as="span" className="text-secondary-500">
            {view.headline}
          </Text>
        </Box>
      )}
    </Box>
  </Flex>
);

// Phone · email · source line.
const ContactRow = ({ view }: { view: ProfileView }) => (
  <Flex align="center" wrap="wrap" gap="4px" mt="12px">
    {view.phone && (
      <Flex align="center" gap="4px">
        <Box as="span" color="secondary-400" display="inline-flex">
          <Phone01 size={14} />
        </Box>
        <Text variant="p" as="span" className="text-secondary-700">
          {view.phone}
        </Text>
      </Flex>
    )}
    {view.phone && view.email && <Dot />}
    {view.email && (
      <Flex align="center" gap="4px">
        <Box as="span" color="secondary-400" display="inline-flex">
          <Mail01 size={14} />
        </Box>
        <Text variant="p" as="span" className="text-secondary-700">
          {view.email}
        </Text>
      </Flex>
    )}
    {view.source && (
      <>
        <Dot />
        <Text variant="p" as="span" className="text-secondary-500">
          Source: {view.source}
        </Text>
      </>
    )}
  </Flex>
);

const SkillPills = ({ skills }: { skills: string[] }) => (
  <Flex wrap="wrap" gap="6px">
    {skills.slice(0, 24).map((s) => (
      <Flex
        key={s}
        align="center"
        bg="secondary-50"
        borderRadius="6px"
        px="8px"
        py="3px"
      >
        <Text variant="p" as="span" className="text-secondary-700">
          {s}
        </Text>
      </Flex>
    ))}
  </Flex>
);

// The CRM record: credit + experience + education + skills. Shown when the
// candidate has no resume on file (a resume takes over the panel instead).
const CrmDetails = ({
  view,
  experiences,
  educations,
}: {
  view: ProfileView;
  experiences: Experience[];
  educations: Education[];
}) => (
  <>
    {view.creditedTo && (
      <Section label="Credited to">
        <Flex align="center" gap="8px">
          <Avatar
            src={view.creditedToAvatar}
            name={view.creditedTo}
            shape="circle"
            size={22}
          />
          <Text variant="p" as="span" className="text-secondary-900">
            {view.creditedTo}
          </Text>
        </Flex>
      </Section>
    )}
    <Section label="Experience">
      {experiences.length > 0 ? (
        <Grid templateColumns="repeat(3, 1fr)" gap="10px">
          {experiences.slice(0, 6).map((e, i) => (
            <ExperienceView key={i} experience={e} />
          ))}
        </Grid>
      ) : (
        <EmptyNote text="No experience found" />
      )}
    </Section>
    <Section label="Education">
      {educations.length > 0 ? (
        <Grid templateColumns="repeat(2, 1fr)" gap="10px">
          {educations.slice(0, 4).map((ed, i) => (
            <EducationView key={i} education={ed} />
          ))}
        </Grid>
      ) : (
        <EmptyNote text="No education found" />
      )}
    </Section>
    <Section label="Skills">
      {view.skills.length > 0 ? (
        <SkillPills skills={view.skills} />
      ) : (
        <EmptyNote text="No skills found" />
      )}
    </Section>
  </>
);

const LoadingState = () => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    gap="10px"
    minH="520px"
  >
    <Spinner
      size="lg"
      thickness="3px"
      speed="0.7s"
      color="primary-500"
      emptyColor="secondary-100"
    />
    <Text variant="caption" as="span" className="text-secondary-500">
      Loading profile…
    </Text>
  </Flex>
);

export const CandidateProfileCard = ({
  candidate,
  active,
  resumes,
  resumesLoading,
}: CandidateProfileCardProps) => {
  const query = useHostContactQuery(candidate.id, active);
  const contact = query.data ?? null;
  const view = buildProfileView(candidate, contact);
  const openFloatingBridge = useAgentForgeStore((s) => s.openFloatingBridge);
  const profilePath = toInternalPath(view.externalHref);

  // Only fetch resumes when the caller didn't already hand us some.
  const resumeQuery = useCandidateProfileQuery(
    candidate.id,
    active && resumes === undefined
  );
  const resumeList =
    resumes ?? fromResumePayloads(resumeQuery.data?.resumes ?? []);

  // Spin while EITHER request is still in flight (no data yet) — the contact
  // record or the resumes — so the body never renders half-loaded or flips
  // from no-resume to resume-only. `isFetching` covers pending + refetch.
  const contactBusy = query.isFetching && !contact;
  const resumesBusy =
    resumes === undefined
      ? resumeQuery.isFetching && !resumeQuery.data
      : !!resumesLoading;
  const loadingDetails = active && (contactBusy || resumesBusy);

  const experiences = Array.isArray(contact?.experiences)
    ? contact!.experiences
    : [];
  const educations = Array.isArray(contact?.educations)
    ? contact!.educations
    : [];

  return (
    <Box
      data-test-id={`agentforge-candidate-profile-${candidate.id}`}
      bg="base-white"
      border="1px solid"
      borderColor="border-color"
      borderRadius="12px"
      boxShadow="0px 8px 24px rgba(38, 34, 68, 0.06)"
      px="20px"
      py="18px"
      // Fixed height (min === max): the card is always the same size and a long
      // profile / embedded resume scrolls inside it, so it never grows or jumps.
      minH="650px"
      maxH="650px"
      overflowY="auto"
    >
      <ProfileHeader
        view={view}
        profilePath={profilePath}
        onOpenProfile={() => openFloatingBridge(profilePath)}
      />

      {loadingDetails ? (
        <LoadingState />
      ) : (
        <>
          <ContactRow view={view} />
          {/* A resume on file takes over the panel; otherwise show the CRM record. */}
          {resumeList.length > 0 ? (
            <Section label="Resume">
              <ResumeSection resumes={resumeList} />
            </Section>
          ) : (
            <CrmDetails
              view={view}
              experiences={experiences}
              educations={educations}
            />
          )}
        </>
      )}
    </Box>
  );
};
